import { useCallback, useMemo, useState } from 'react'

import { getFractionTypeQuestionData as gFracQues } from './FractionTypes.utils'

interface UseFractionTypesReturnType {
  numerator: number
  denominator: number
  quotient: number
  formValid: boolean
  selectedAnswer?: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useFractionTypes({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionTypesReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [selectedAnswer, setSelectedAnswer] = useState<string>()
  const question = useMemo(() => gFracQues(difficultyLevel, taskHistory), [difficultyLevel, taskHistory])

  const handleCheckAnswer = useCallback((): void => {
    if (!selectedAnswer || !question?.type) return
    onAnswerStateChange(selectedAnswer === question.type)
  }, [selectedAnswer, question, onAnswerStateChange])

  const onInputChange = useCallback((val?: string) => setSelectedAnswer(val), [])

  const goToNextQuestion = useCallback(() => {
    const { numerator, denominator, quotient } = question
    const newTaskHistory = [`${quotient}-${numerator}-${denominator}`, ...taskHistory].slice(0, 3)
    setTaskHistory(newTaskHistory)
    onInputChange()
    onNextQuestionClick()
  }, [taskHistory, onInputChange, onNextQuestionClick, question])

  return {
    numerator: question.numerator,
    denominator: question.denominator,
    quotient: question.quotient,
    formValid: !!selectedAnswer,
    selectedAnswer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useFractionTypes
